// Accordion (SSR-first) – works in server components with native <details>/<summary> semantics.

import { cn } from '@/lib/ui/cn';
import {
  PropsWithChildren,
  useId,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

export type AccordionProps = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren & {
    id?: string; // optional stable id for the root element
  };

export const Accordion = ({
  id,
  className,
  children,
  ...rest
}: AccordionProps) => {
  // Accessibility is handled via native <details>/<summary> semantics.
  const generatedId = useId();
  const rootId = id ?? `acc-${generatedId}`;

  return (
    <div
      id={rootId}
      data-accordion-root
      className={['w-full', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
};

export interface AccordionItemProps extends HTMLAttributes<HTMLDivElement> {
  value?: string; // optional stable value; useful for testing/analytics
  heading: ReactNode; // label text / node for the summary
  open?: boolean; // initial open state (SSR) – maps to <details open>
  showToggleText?: boolean; // whether to show "View"/"Close" text next to the toggle icon
  backgroundColor?: string; // background color for the panel
  innerBackgroundColor?: string; // background color for the panel when expanded
  innerPadding?: string; // padding for the panel when expanded
  headingFontClassName?: string; // font size for the heading
  summaryClassName?: string; // additional class names for the summary
  children: ReactNode; // panel content
}

export const AccordionItem = ({
  value,
  heading,
  open = false,
  showToggleText = true,
  backgroundColor = 'bg-sh-cream',
  innerBackgroundColor = 'bg-sh-cream',
  innerPadding = 'p-4 sm:p-6',
  headingFontClassName = 'sh-heading-3',
  summaryClassName = '',
  children,
  className,
  ...rest
}: AccordionItemProps) => (
  <section
    className={[backgroundColor, className].filter(Boolean).join(' ')}
    {...rest}
  >
    <details
      className='group'
      data-accordion-item
      data-accordion-value={value ?? undefined}
      {...(open ? { open: true } : {})}
    >
      <summary
        className={cn(
          summaryClassName,
          'flex w-full cursor-pointer list-none items-center justify-between gap-4 px-7 py-8 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:px-22 sm:py-10'
        )}
      >
        <span className={cn(headingFontClassName)}>{heading}</span>
        <span className='flex items-center gap-3'>
          {showToggleText && (
            <span
              className={cn(headingFontClassName, 'mr-12 hidden sm:inline')}
            >
              <span className='group-open:hidden'>View</span>
              <span className='hidden group-open:inline'>Close</span>
            </span>
          )}
          <ChevronRight className='h-8 w-4 transition-transform duration-200 group-open:rotate-90' />
        </span>
      </summary>
      <div className='grid grid-rows-[0fr] overflow-hidden transition-[grid-template-rows] duration-300 ease-out group-open:grid-rows-[1fr]'>
        <div className='min-h-0'>
          <div className={cn(innerBackgroundColor, innerPadding)}>
            {children}
          </div>
        </div>
      </div>
    </details>
  </section>
);

export const ChevronRight = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox='0 0 17 32'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
    {...props}
  >
    <path
      d='M1 31L16 16L0.999999 0.999999'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);
